import PageHeader from '@app/components/header/PageHeader';
import PageHeaderTitle from '@app/components/header/PageHeaderTitle';
import Loader from '@app/components/Loader';
import PageWithHeader from '@app/components/PageWithHeader';
import Tabs, { TabPane } from '@app/components/Tabs';
import ManageOrganizationUsers from '@app/components/userManagement/ManageOrganizationUsers';
import {
  transformKnoxerIdentityProviders,
  UpdateOrganizationIdentityProvidersRequest,
} from '@app/domain/userManagement/identityProviders';
import useLoadable from '@app/hooks/loadable/useLoadable';
import useAppStores from '@app/hooks/useAppStores';
import useStagedIds, { StagedIds } from '@app/hooks/useStagedIds';
import useInfraStores from '@app/hooks/useInfraStores';
import {
  getAllIdentityProvidersOfOrganization,
  updateIdentityProvidersOfOrganization,
} from '@app/services/userManagement/organizationIdentityProviderServices';
import browserHistory from '@app/utils/browserHistory';
import { LoadableCreator } from '@app/utils/Loadable';
import { shoot } from '@app/utils/messageLauncher';
import BackeeMode from '@backee/backeeMode';
import ManageOrganizationIdentityProviders from '@backee/routes/organizations/manageOrganizationPage/ManageOrganizationIdentityProviders';
import ManageOrganizationServices from '@backee/routes/organizations/manageOrganizationPage/ManageOrganizationServices';
import { updateServicesOfOrganization } from '@backee/services/organizationServicesServices';
import { UpdateOrganizationServicesRequest } from '@backee/domain/organizationServices';
import { getOrganizationById, transformToOrganization } from '@backee/services/organizationsServices';
import { observer } from 'mobx-react';
import React, { FC, ReactNode } from 'react';
import { useParams } from 'react-router-dom';

// Create a wrapper component that explicitly includes children
const TabPaneWithChildren = (props: React.ComponentProps<typeof TabPane> & { children: ReactNode }): JSX.Element => (
  <TabPane {...props} />
);

export enum ManageOrganizationTabType {
  services = 'services',
  users = 'users',
  emailKnoxerIdentityProviders = 'emailKnoxerIdentityProviders',
  phoneKnoxerIdentityProviders = 'phoneKnoxerIdentityProviders',
}

const ManageOrganizationPage: FC = observer(() => {
  const { navigationStore, permissionsStore } = useInfraStores<BackeeMode>();
  const { knoxersStore, identityProvidersStore, userRolesStore } = useAppStores<BackeeMode>();
  const { organizationId, activeTab } = useParams<{ organizationId: string; activeTab?: string }>();

  function onActiveTabChange(newActiveTabKey: ManageOrganizationTabType): void {
    browserHistory.replace(navigationStore.generateManageOrganizationLink(organizationId, newActiveTabKey));
  }

  function getValidActiveTab(currentActiveTabKey: string | undefined): ManageOrganizationTabType {
    if (
      !currentActiveTabKey ||
      !Object.values(ManageOrganizationTabType).includes(currentActiveTabKey as ManageOrganizationTabType)
    ) {
      return ManageOrganizationTabType.services;
    }

    return currentActiveTabKey as ManageOrganizationTabType;
  }

  const [organizationLoadable, , setOrganizationLoadable] = useLoadable(async () => {
    const organizationServerResponses = await getOrganizationById(organizationId);
    return transformToOrganization(organizationServerResponses);
  }, [organizationId]);

  const onSaveServices = async (change: UpdateOrganizationServicesRequest): Promise<void> => {
    const updatedOrganization = await updateServicesOfOrganization(organizationId, change);
    setOrganizationLoadable(LoadableCreator.resolved(transformToOrganization(updatedOrganization)));
    shoot({ type: 'success', closeable: true }, 'Services saved');
  };

  const [allOrganizationIdentityProvidersLoadable, reloadAllOrganizationIdentityProviders] = useLoadable(async () => {
    const allOrganizationIdentityProviders = await getAllIdentityProvidersOfOrganization(organizationId);
    return allOrganizationIdentityProviders.map(transformKnoxerIdentityProviders);
  }, [organizationId]);

  const knoxers = knoxersStore.knoxers;

  function getActiveIdentityProviderNames(knoxerId: string | undefined): string[] {
    return (
      allOrganizationIdentityProvidersLoadable.result
        ?.find((knoxerIdentityProviders) => knoxerIdentityProviders.knoxerId === knoxerId)
        ?.identityProviders.map((identityProvider) => identityProvider.name) ?? []
    );
  }

  // Held here rather than in each tab, since an inactive tab is unmounted and would lose its staged changes
  const servicesStagedIds = useStagedIds(organizationLoadable.result?.serviceIds ?? []);
  const emailKnoxerIdentityProvidersStagedIds = useStagedIds(getActiveIdentityProviderNames(knoxers.result?.emailKnoxer.id));
  const phoneKnoxerIdentityProvidersStagedIds = useStagedIds(getActiveIdentityProviderNames(knoxers.result?.phoneKnoxer.id));

  if (!knoxers.isResolved()) {
    return <Loader spinning />;
  }

  const onSaveIdentityProviders = async (knoxerId: string, change: UpdateOrganizationIdentityProvidersRequest): Promise<void> => {
    await updateIdentityProvidersOfOrganization(organizationId, knoxerId, change);
    await reloadAllOrganizationIdentityProviders.silent();
    shoot({ type: 'success', closeable: true }, 'Identity providers saved');
  };

  function renderIdentityProvidersForKnoxer(knoxerId: string, stagedIds: StagedIds, id: string, dataTestId: string): ReactNode {
    const allIdentityProvidersOfKnoxer = identityProvidersStore.getAllIdentityProvidersForKnoxer(knoxerId);

    return allOrganizationIdentityProvidersLoadable.resolve(
      (): ReactNode => (
        <ManageOrganizationIdentityProviders
          id={id}
          dataTestId={dataTestId}
          identityProviders={allIdentityProvidersOfKnoxer}
          stagedIds={stagedIds}
          onSave={(change): Promise<void> => onSaveIdentityProviders(knoxerId, change)}
        />
      ),
      () => 'Loading...',
    );
  }

  return (
    <PageWithHeader
      width='full'
      paddingTop='none'
      header={
        <PageHeader backButtonTo={navigationStore.generateOrganizationsListLink()}>
          <PageHeaderTitle
            category='ORGANIZATION'
            title={organizationLoadable.resolve(
              (organization) => organization.name,
              () => 'Loading...',
            )}
          />
        </PageHeader>
      }
    >
      <Tabs tabBarGutter={0} onChange={onActiveTabChange} activeKey={getValidActiveTab(activeTab)} destroyInactiveTabPane>
        <TabPaneWithChildren
          tab={
            <span data-testid={`tab-manage-organization-services${organizationLoadable.isResolved() ? '' : '-disabled'}`}>
              Services
            </span>
          }
          key={ManageOrganizationTabType.services}
          disabled={!organizationLoadable.isResolved()}
        >
          {organizationLoadable.resolve(
            (organization): ReactNode => (
              <ManageOrganizationServices stagedIds={servicesStagedIds} onSave={onSaveServices} />
            ),
            () => 'Loading...',
          )}
        </TabPaneWithChildren>
        <TabPaneWithChildren
          tab={
            <span data-testid={`tab-manage-organization-users${organizationLoadable.isResolved() ? '' : '-disabled'}`}>
              Users
            </span>
          }
          key={ManageOrganizationTabType.users}
          disabled={!organizationLoadable.isResolved()}
        >
          {LoadableCreator.combine(organizationLoadable, allOrganizationIdentityProvidersLoadable, knoxers).resolve(
            ([organization, organizationIdentityProviders, knoxers]): ReactNode => (
              <ManageOrganizationUsers
                organization={organization}
                knoxers={knoxers}
                organizationIdentityProviders={organizationIdentityProviders}
                canViewNsknoxUsers={true}
                canAddUsersToOrg={permissionsStore.isEditAllowed}
                canSuspendUserFromOrganization={permissionsStore.isEditAllowed}
                canEditUserPermissions={permissionsStore.isEditAllowed}
                canKillUserSessions={permissionsStore.isEditAllowed}
                globalWebAppRoleTypes={permissionsStore.isEditGlobalRolesAllowed ? userRolesStore.userRoleTypes : undefined}
              />
            ),
            () => 'Loading...',
          )}
        </TabPaneWithChildren>
        <TabPaneWithChildren
          tab={
            <span
              data-testid={`tab-manage-organization-email-knoxer-identity-providers${
                allOrganizationIdentityProvidersLoadable.isResolved() ? '' : '-disabled'
              }`}
            >
              Identity Providers - {knoxers.result.emailKnoxer.name} Knoxer
            </span>
          }
          key={ManageOrganizationTabType.emailKnoxerIdentityProviders}
          disabled={!allOrganizationIdentityProvidersLoadable.isResolved()}
        >
          {renderIdentityProvidersForKnoxer(
            knoxers.result.emailKnoxer.id,
            emailKnoxerIdentityProvidersStagedIds,
            'organization-manage-email-knoxer-identity-providers-table',
            'organization-manage-email-knoxer-identity-providers-table',
          )}
        </TabPaneWithChildren>
        <TabPaneWithChildren
          tab={
            <span
              data-testid={`tab-manage-organization-phone-knoxer-identity-providers${
                allOrganizationIdentityProvidersLoadable.isResolved() ? '' : '-disabled'
              }`}
            >
              Identity Providers - {knoxers.result.phoneKnoxer.name} Knoxer
            </span>
          }
          key={ManageOrganizationTabType.phoneKnoxerIdentityProviders}
          disabled={!allOrganizationIdentityProvidersLoadable.isResolved()}
        >
          {renderIdentityProvidersForKnoxer(
            knoxers.result.phoneKnoxer.id,
            phoneKnoxerIdentityProvidersStagedIds,
            'organization-manage-phone-knoxer-identity-providers-table',
            'organization-manage-phone-knoxer-identity-providers-table',
          )}
        </TabPaneWithChildren>
      </Tabs>
    </PageWithHeader>
  );
});

export default ManageOrganizationPage;
